import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  AgentResponse,
  PaginatedAgentResponse,
  AgentDetailApiResponse,
  ProviderAccountResponse,
  ProviderAccountCreate,
  ProviderAccountUpdate,
  PaginatedProviderAccountResponse,
  ApiProviderAccountResponse,
} from './types';

export interface AiAgentsApi {
  listAgents(options?: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedAgentResponse>;

  getAgent(agentId: string): Promise<AgentResponse>;

  deleteAgent(agentId: string): Promise<void>;

  listProviderAccounts(options?: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedProviderAccountResponse>;

  createProviderAccount(
    data: ProviderAccountCreate,
  ): Promise<ProviderAccountResponse>;

  getProviderAccount(accountId: string): Promise<ProviderAccountResponse>;

  updateProviderAccount(
    accountId: string,
    data: ProviderAccountUpdate,
  ): Promise<ProviderAccountResponse>;

  deleteProviderAccount(accountId: string): Promise<void>;
}

export const aiAgentsApiRef = createApiRef<AiAgentsApi>({
  id: 'plugin.ai-agents.api',
});

export class AiAgentsClient implements AiAgentsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('ai-agents');
  }

  async listAgents(options?: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedAgentResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.page) params.append('page', String(options.page));
    if (options?.pageSize) params.append('page_size', String(options.pageSize));

    const qs = params.toString();
    const url = `${baseUrl}/agents${qs ? `?${qs}` : ''}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to list agents: ${response.statusText}`);
    }
    return response.json();
  }

  async getAgent(agentId: string): Promise<AgentResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/agents/${encodeURIComponent(agentId)}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get agent: ${response.statusText}`);
    }
    const result: AgentDetailApiResponse = await response.json();
    return result.data;
  }

  async deleteAgent(agentId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/agents/${encodeURIComponent(agentId)}`;

    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw new Error(`Failed to delete agent: ${response.statusText}`);
    }
  }

  async listProviderAccounts(options?: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedProviderAccountResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.page) params.append('page', String(options.page));
    if (options?.pageSize) params.append('page_size', String(options.pageSize));

    const qs = params.toString();
    const url = `${baseUrl}/provider-accounts${qs ? `?${qs}` : ''}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to list provider accounts: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async createProviderAccount(
    data: ProviderAccountCreate,
  ): Promise<ProviderAccountResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/provider-accounts`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(
        `Failed to create provider account: ${response.statusText}`,
      );
    }
    const result: ApiProviderAccountResponse = await response.json();
    return result.data;
  }

  async getProviderAccount(
    accountId: string,
  ): Promise<ProviderAccountResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/provider-accounts/${encodeURIComponent(accountId)}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get provider account: ${response.statusText}`);
    }
    const result: ApiProviderAccountResponse = await response.json();
    return result.data;
  }

  async updateProviderAccount(
    accountId: string,
    data: ProviderAccountUpdate,
  ): Promise<ProviderAccountResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/provider-accounts/${encodeURIComponent(accountId)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(
        `Failed to update provider account: ${response.statusText}`,
      );
    }
    const result: ApiProviderAccountResponse = await response.json();
    return result.data;
  }

  async deleteProviderAccount(accountId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/provider-accounts/${encodeURIComponent(accountId)}`;

    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw new Error(
        `Failed to delete provider account: ${response.statusText}`,
      );
    }
  }
}
